import { NavLink } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Clipboard, BarChart3, Ship, LogOut, FileSpreadsheet, Users, Tag, PanelLeftClose, PanelLeftOpen, Grid3X3, Table2, Sheet, Wand2, History, UserCog, Archive, Package, MessageSquareText, CalendarRange, ReceiptText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { to: "/bookings", icon: CalendarDays, label: "Rezervasyonlar" },
  { to: "/sheet", icon: FileSpreadsheet, label: "Operasyon Sayfasi" },
  { to: "/live-sheet", icon: Sheet, label: "Canli Sayfa" },
  { to: "/gunluk-ozet", icon: CalendarRange, label: "Günlük Özet" },
  { to: "/faturalar", icon: ReceiptText, label: "Faturalar" },
  { to: "/garsonlar", icon: UserCog, label: "Garsonlar" },
  { to: "/reviews", icon: MessageSquareText, label: "Yorumlar", adminOnly: true },
  { to: "/backups", icon: Archive, label: "Yedekler" },
  { to: "/operations", icon: Clipboard, label: "Operasyon" },
  { to: "/analytics", icon: BarChart3, label: "Analitik" },
  { to: "/staff", icon: Users, label: "Personel", adminOnly: true },
  { to: "/tags", icon: Tag, label: "Etiketler", adminOnly: true },
  { to: "/rules", icon: Wand2, label: "Dağıtım Kuralları", adminOnly: true },
  { to: "/product-catalog", icon: Package, label: "Ürün Tanımları", adminOnly: true },
  { to: "/audit", icon: History, label: "Kullanıcı Geçmişi", adminOnly: true },
  { to: "/table-layout", icon: Grid3X3, label: "Masa Duzeni" },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout, isSuperAdmin, isGarson } = useAuth();
  const visibleItems = navItems.filter(item => {
    if (isGarson) return item.to === "/live-sheet";
    if (item.adminOnly) return isSuperAdmin;
    return true;
  });

  if (collapsed) {
    return (
      <aside className="sidebar" style={{ width: 56, minWidth: 56, padding: 0 }} data-testid="sidebar-collapsed">
        <div className="flex flex-col items-center py-3 h-full">
          <button onClick={onToggle} className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg mb-4" data-testid="sidebar-open-btn">
            <PanelLeftOpen className="w-5 h-5" />
          </button>
          <div className="flex-1 flex flex-col items-center gap-1 py-2">
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                title={item.label}
                className={({ isActive }) =>
                  `p-2 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-500 hover:text-slate-300 hover:bg-white/5"}`
                }
              >
                <item.icon className="w-4 h-4" />
              </NavLink>
            ))}
          </div>
          <button onClick={logout} className="p-2 text-slate-500 hover:text-red-400 rounded-lg" title="Cikis">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo + Collapse */}
      <div className="px-6 py-6 border-b border-white/[0.04] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Ship className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-base font-bold text-white tracking-tight leading-none">
              Mega Lufer
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-400/60 mt-1">
              Booking Panel
            </div>
          </div>
        </div>
        <button onClick={onToggle} className="p-1.5 text-slate-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="sidebar-close-btn" title="Sidebar'i kapat">
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3" data-testid="sidebar-nav">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 px-3 mb-2">
          Menu
        </div>
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 mb-1 group ${
                isActive
                  ? "bg-gradient-to-r from-blue-600/20 to-blue-500/10 text-blue-400 border border-blue-500/15"
                  : "text-slate-400 hover:bg-white/[0.03] hover:text-slate-200 border border-transparent"
              }`
            }
          >
            <item.icon className="w-4 h-4 transition-transform group-hover:scale-110" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="px-5 py-4 border-t border-white/[0.04]">
        {user && (
          <div className="mb-3">
            <div className="text-xs text-slate-400 font-medium" data-testid="sidebar-username">
              {user.name || user.email}
            </div>
            <div className="text-[10px] text-slate-600">{user.email}</div>
          </div>
        )}
        <button
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          Cikis Yap
        </button>
        <div className="flex items-center gap-2 mt-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
            Sistem Aktif
          </span>
        </div>
      </div>
    </aside>
  );
}
